import uniqBy from 'utils/uniqBy';

import ancestryApi from '../api/ancestry';
import generatorApi, { getDirectionFromTreeType } from '../api/generator';
import { extractPhotosFromPages, transformPagesFromServer } from './utils';
import { getAsynchronouslyGeneratedData } from './socket';

/**
 * Generate data from MyCanvas source.
 * @param {string} accessToken
 * @param {object} template
 * @param {object} config
 * @param {string} config.treeId
 * @param {string} config.treeName
 * @param {string} config.personGid
 * @param {string} config.partnerGid
 * @param {Array<Array<string>>} config.arrayOfPersonGids
 * @param {number} config.numberOfGenerations
 * @param {Array<string>} config.optionIds
 * @param {boolean} config.withoutCover
 * @param {Array<object>} currentPhotos
 * @param {void} onProgress
 * @returns {{ data: object, error: object }}
 */
const generateMyCanvasData = async (
  accessToken,
  template,
  config = {},
  currentPhotos = [],
  onProgress = () => { },
) => {
  const { postLog } = ancestryApi(accessToken);

  const arrayOfPersons = [];
  if (config.arrayOfPersonGids) {
    arrayOfPersons.push(...config.arrayOfPersonGids);
  } else {
    arrayOfPersons.push([config.personGid, config.partnerGid].filter(id => !!id));
  }

  const direction = getDirectionFromTreeType(config.treeType, template.id);

  const { data: socketData, error: socketError } = await generatorApi(
    accessToken,
    template,
    true,
  ).getMyCanvasData(
    config.optionIds,
    arrayOfPersons.map(persons => ({
      persons,
      numberOfGenerations: config.numberOfGenerations,
      treeName: config.treeName,
      direction,
    })),
  );

  if (socketError) {
    postLog(
      `[${socketError.code}] Problem with mycanvas async generator`,
      {
        sku: template.id,
        version: template.version,
        persons: arrayOfPersons,
        optionIds: config.optionIds,
        numberOfGenerations: config.numberOfGenerations,
        direction,
      },
      socketError.body,
    );

    return {
      error:
        'We encountered an error while creating your project with the data from MyCanvas and our team has been automatically notified. We are actively working with Ancestry to identify this data error and will contact you once you will be able to try again.',
    };
  }

  const { data: dataArray, error } = await getAsynchronouslyGeneratedData(
    socketData.url,
    onProgress,
  );

  if (error) {
    postLog(
      `[WSS] Problem with mycanvas async generator`,
      {
        sessionId: socketData.id,
        sku: template.id,
        version: template.version,
        persons: arrayOfPersons,
        optionIds: config.optionIds,
        numberOfGenerations: config.numberOfGenerations,
        direction,
      },
      error,
    );

    return { error };
  }

  if (dataArray.length === 0 || dataArray.length !== arrayOfPersons.length) {
    postLog(
      `Generator returns only ${dataArray.length} data items, but was required ${arrayOfPersons.length} items.`,
      {
        sessionId: socketData.id,
        sku: template.id,
        version: template.version,
        persons: arrayOfPersons,
        optionIds: config.optionIds,
        numberOfGenerations: config.numberOfGenerations,
        direction,
      },
    );

    return {
      error:
        'We encountered an error while creating your project with the data from MyCanvas and our team has been automatically notified. We are actively working with Ancestry to identify this data error and will contact you once you will be able to try again.',
    };
  }

  const data = {
    cover: dataArray[0].cover,
    pages: dataArray.reduce((acc, item) => {
      if (item.pages) {
        acc.push(...item.pages);
      }
      return acc;
    }, []),
  };

  const withCover = data.cover && config.withoutCover !== true;

  let photos = [...currentPhotos];
  if (withCover) {
    photos.push(...extractPhotosFromPages(template, [data.cover]));
  }
  if (data.pages) {
    photos.push(...extractPhotosFromPages(template, data.pages));
  }

  photos = uniqBy(photos, 'key');

  const pages = [];
  if (withCover) {
    pages.push(...transformPagesFromServer(template, 'cover', photos, [data.cover]));
  }
  if (data.pages) {
    pages.push(...transformPagesFromServer(template, 'pages', photos, data.pages));
  }

  if (pages.length === 0) {
    return {
      error: "Oops! We didn't find anything in MyCanvas.",
    };
  }

  return {
    data: {
      additionalInfo: {
        source: 'mycanvas',
        treeId: config.treeId,
        treeName: config.treeName,
        optionIds: config.optionIds || undefined,
      },
      photos,
      pages,
    },
    session: {
      id: socketData.id,
    }
  };
};

export default generateMyCanvasData;
